import { useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { usersQuery } from '../../lib/users'
import { Button } from '../../components/ui/button'
import { EmptyState } from '../../components/ui/empty-state'
import { ScrollText } from 'lucide-react'

// DIS-42: Settings › Audit log (admin): newest first, filter by actor/action/date.
export const Route = createFileRoute('/_app/settings/audit')({
  beforeLoad: ({ context }) => {
    if (context.me.role !== 'admin') throw redirect({ to: '/' })
  },
  component: AuditPage,
})

const ACTIONS = ['user.role', 'user.deactivate', 'user.reactivate', 'invite.create', 'invite.revoke', 'workspace.update', 'channel.settings', 'webhook.create', 'webhook.update', 'webhook.regenerate', 'webhook.delete', 'emoji.create', 'emoji.delete']

function AuditPage() {
  const users = useQuery(usersQuery)
  const byId = new Map((users.data ?? []).map((u) => [u.id, u]))
  const [actor, setActor] = useState('')
  const [action, setAction] = useState('')
  const [after, setAfter] = useState('')
  const [before, setBefore] = useState('')
  const q = useInfiniteQuery({
    queryKey: ['admin', 'audit', actor, action, after, before],
    initialPageParam: '',
    queryFn: async ({ pageParam }) => {
      const { data, response } = await api.GET('/admin/audit', { params: { query: { ...(actor ? { actor } : {}), ...(action ? { action } : {}), ...(after ? { after } : {}), ...(before ? { before } : {}), ...(pageParam ? { cursor: pageParam } : {}), limit: 50 } } })
      if (!data) throw new Error(`GET /admin/audit failed: ${response.status}`)
      return data
    },
    getNextPageParam: (last) => last.next_cursor || undefined,
  })
  const entries = (q.data?.pages ?? []).flatMap((p) => p.entries)
  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 p-6">
      <div>
        <h1 className="text-lg font-semibold">Audit log</h1>
        <p className="text-[13px] text-fg-muted">Every admin action, append-only.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[12px]" data-testid="audit-filters">
        <select aria-label="Actor" value={actor} onChange={(e) => setActor(e.target.value)} className="h-7 rounded-md border border-border bg-bg px-1.5">
          <option value="">Any actor</option>
          {(users.data ?? []).filter((u) => u.role === 'admin').map((u) => (
            <option key={u.id} value={u.id}>{u.display_name}</option>
          ))}
        </select>
        <select aria-label="Action" value={action} onChange={(e) => setAction(e.target.value)} className="h-7 rounded-md border border-border bg-bg px-1.5">
          <option value="">Any action</option>
          {ACTIONS.map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <label className="flex items-center gap-1 text-fg-muted">
          After <input type="date" aria-label="After" value={after} onChange={(e) => setAfter(e.target.value)} className="h-7 rounded-md border border-border bg-bg px-1.5 text-fg" />
        </label>
        <label className="flex items-center gap-1 text-fg-muted">
          Before <input type="date" aria-label="Before" value={before} onChange={(e) => setBefore(e.target.value)} className="h-7 rounded-md border border-border bg-bg px-1.5 text-fg" />
        </label>
      </div>
      {q.isSuccess && entries.length === 0 && <EmptyState icon={<ScrollText size={16} />} title="Nothing logged yet" hint="Admin actions show up here as they happen." />}
      <ol className="divide-y divide-border rounded-lg border border-border" data-testid="audit-list">
        {entries.map((e) => {
          const actorUser = e.actor_id ? byId.get(e.actor_id) : undefined
          return (
            <li key={e.id} className="flex items-start gap-3 px-3 py-2 text-[13px]" data-testid="audit-entry" data-action={e.action}>
              <time className="w-36 shrink-0 text-fg-muted" dateTime={e.created_at}>{new Date(e.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}</time>
              <span className="w-32 shrink-0 truncate font-medium">{actorUser?.display_name ?? 'Unknown'}</span>
              <span className="w-36 shrink-0 font-mono text-[12px]">{e.action}</span>
              <span className="min-w-0 flex-1 truncate text-fg-muted" title={JSON.stringify(e.meta)}>
                {e.target_type}
                {e.target_id ? ` ${e.target_id}` : ''} {Object.keys(e.meta ?? {}).length ? JSON.stringify(e.meta) : ''}
              </span>
              {e.ip && <span className="shrink-0 text-[11px] text-fg-subtle">{e.ip}</span>}
            </li>
          )
        })}
      </ol>
      {q.hasNextPage && (
        <Button variant="ghost" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
          {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  )
}
